import type { Dayjs } from 'dayjs';
import { MOUSE_ACTION_TYPE, UPDATE_TYPE } from '../utils/constants';

export enum TIMELINE_SIDE {
	LEFT = 'left',
	RIGHT = 'right',
	BILATERAL = 'bilateral'
}

export type TimePosition = 'start' | 'end';

export type SideType = TIMELINE_SIDE.LEFT | TIMELINE_SIDE.RIGHT | TIMELINE_SIDE.BILATERAL;

export interface CycleCreationResult<T extends CycleCallback = CycleCallback> {
	success: boolean;
	cycleData?: T;
}

export interface CycleCallback {
	id: string;
	name?: string;
	type_id?: string;
	start_time: Dayjs;
	end_time: Dayjs;
	identified: boolean;
	is_justified?: boolean;
	side?: SideType;
	paired_cycle_id?: string;
	is_persisted?: boolean;
}

export type OverlapColors = Record<number, string>;

export interface TimelineTheme {
	colors: {
		identifiedCycle: {
			background: string;
			border: string;
			handles: string;
			hoverBackground: string;
		};
		unidentifiedCycle: {
			background: string;
			handles: string;
			hoverBackground: string;
		};
		outOfRangeCycle?: {
			background: string;
			selectedBackground: string;
			border: string;
			handles: string;
		};
		overlap?: OverlapColors;
		error?: string;
		ruler: string;
		timeNeedle: string;
		background: string;
	};
	dimensions: {
		cycleBarHeight: number;
		markerRadius: number;
		markerWidth: number;
		needleWidth: number;
		triangleSize: number;
		lineWidth: number;
		borderWidth: number;
		verticalOffset: number;
	};
}

export interface ValidationError {
	type: 'overlap' | 'bounds' | 'duration' | 'video_duration_exceeded';
	cycleId: string;
	message: string;
	conflictWith?: string;
	severity: 'warning' | 'error';
}

export interface ValidationResult {
	isValid: boolean;
	errors: ValidationError[];
}

export interface TimelineConfig {
	margin?: number;
	majorHeight?: number;
	minorHeight?: number;
}

export interface TimeScale {
	majorInterval: number;
	minorInterval: number;
	majorHeight: number;
	minorHeight: number;
}

export interface CycleBounds {
	startX: number;
	endX: number;
}

export interface CycleData {
	id: string;
	name?: string;
	type_id?: string;
	start: number;
	end: number;
	identified: boolean;
	is_justified?: boolean;
	side?: SideType;
	paired_cycle_id?: string;
	selected?: boolean;
	editable?: boolean;
}

export enum InteractionType {
	START_RESIZE = 'start-resize',
	END_RESIZE = 'end-resize',
	MOVE = 'move',
	SELECT = 'select',
	EDIT = 'edit',
	NONE = 'none'
}

export type CursorType = 'default' | 'ew-resize' | 'col-resize' | 'pointer' | 'grab' | 'grabbing';

export interface InteractionResult {
	type: InteractionType;
	cycleId?: string;
	dragOffset?: number;
}

export interface CursorResult {
	cursor: CursorType;
	hoveredCycleId?: string;
}

export interface InteractionPermissions {
	isInEditMode?: boolean;
	editingCycleId?: string | null;
}

export interface TimelineOperationData {
	videoDuration: number;
	cycles: CycleData[];
}

export interface SpaceSearchResult {
	start: number;
	end: number;
	success: boolean;
	adjustedDuration?: boolean;
}

export interface DragState {
	isDragging: boolean;
	interactionType: InteractionType;
	cycleId: string | null;
	dragOffset: number;
}

export interface TooltipPosition {
	x: number;
	y: number;
	time: number;
}

export type BilateralFields = {
	paired_cycle_id?: string;
	side?: SideType;
};

export interface UnidentifiedCycleResult {
	interaction?: InteractionResult;
	cursor?: CursorType;
	cycleId?: string;
}

export interface MouseDownAction {
	type: MouseActionType;
	cycleId?: string;
	shouldPreventDefault: boolean;
}

export interface CycleSnapResult {
	finalStart: number;
	finalEnd: number;
	snapIndicatorTime: number | null;
}

export interface CycleCheck {
	cycle: CycleData;
	isNearStart: boolean;
	isNearEnd: boolean;
	isMouseOnCycle: boolean;
	dragOffset: number;
}

export type UpdateType = (typeof UPDATE_TYPE)[keyof typeof UPDATE_TYPE];

export type MouseActionType = (typeof MOUSE_ACTION_TYPE)[keyof typeof MOUSE_ACTION_TYPE];

export interface BilateralDragPreview {
	cycleId: string;
	pairedCycleId?: string;
	start: number;
	end: number;
	isValid: boolean;
}

export interface TimelineProps {
	value: CycleCallback[];
	onChange: (cycles: CycleCallback[]) => void;
	videoDuration: number;
	currentTime?: number;
	theme?: TimelineTheme;
	onValidationError?: (error: ValidationError | null) => void;
	onInteractionError?: (error: ValidationError | null) => void;
	selectedCycleId?: string | null;
	selectedCycleIds?: string[];
	onCycleSelect?: (cycleId: string | null) => void;
	onSeek?: (time: number) => void;
	isEditMode?: boolean;
	enableBilateralMirroring?: boolean;
	allCycles?: CycleCallback[];
	side?: SideType;
	onPauseVideo?: () => void;
	onScrubbingChange?: (isScrubbing: boolean) => void;
	onCycleDragChange?: (isDragging: boolean) => void;
	onCycleHover?: (cycleId: string | null) => void;
	bilateralDragPreview?: BilateralDragPreview | null;
	onBilateralDragPreviewChange?: (preview: BilateralDragPreview | null) => void;
	allowOverlap?: boolean;
	overlapColors?: OverlapColors;
	outOfRangeCycleIds?: string[];
	outOfRangeFromTime?: number;
	highlightedCycleIds?: string[];
}

export interface TimelineConfig {
	id: string;
	label?: string;
	side: SideType | string;
	filter?: (cycle: CycleCallback) => boolean;
	timelineProps?: Partial<TimelineProps>;
}

export interface AddCycleOptions {
	sides: (SideType | string)[];
	clickTime?: number;
	duration?: number;
	linkAsPairs?: boolean;
	metadata?: Record<string, unknown>;
}

export interface SpaceAvailability {
	left: boolean;
	right: boolean;
	bilateral: boolean;
}

export interface TimelineHelpers {
	addCycles: (options: AddCycleOptions) => void;
	addBilateralPair: (duration?: number) => void;
	addSingleCycle: (side: SideType | string, duration?: number) => void;
	updateCycle: (cycleId: string, updates: Partial<CycleCallback>) => void;
	updateCycleTime: (cycleId: string, startTime: number, endTime: number, mirrorPaired?: boolean) => void;
	removeCycle: (cycleId: string) => void;
	removeCycleWithPair: (cycleId: string) => void;
	getCyclesByTimeline: (timelineId: string) => CycleCallback[];
	getCyclesBySide: (side: SideType | string) => CycleCallback[];
	getCycleById: (cycleId: string) => CycleCallback | undefined;
	getPairedCycle: (cycleId: string) => CycleCallback | undefined;
	validate: () => { isValid: boolean; errors: ValidationError[] };
	handleSideChange: (newMode: SideType, options?: { onError?: (message: string) => void }) => void;
	hasSpaceAvailable: (side: SideType) => boolean;
}
